/**
 * ID comparison and normalization utilities
 * 
 * Handles the reality that our system has multiple ID formats:
 * - Django: numeric IDs (123)
 * - Fulcrum: UUID strings ("ee123955-7b1e-491d-87f1-b3a2e25b9b69")
 * - GeoJSON: string representations of IDs ("123")
 */

/**
 * Compare two IDs regardless of their type (string vs number)
 * 
 * @param id1 - First ID to compare
 * @param id2 - Second ID to compare
 * @returns true if IDs represent the same entity
 * 
 * @example
 * idsEqual('123', 123)     // → true
 * idsEqual('22', 22)       // → true  
 * idsEqual('uuid-123', 'uuid-123') // → true
 * idsEqual(null, undefined) // → false
 */
export function idsEqual(id1: string | number | null | undefined, id2: string | number | null | undefined): boolean {
  // Handle null/undefined cases
  if (id1 == null || id2 == null) {
    return id1 === id2;
  }
  
  // Convert both to strings for comparison
  return String(id1) === String(id2);
}

/**
 * Normalize an ID to a string for consistent handling
 * 
 * @param id - ID to normalize
 * @returns string representation of the ID, or null if invalid
 */
export function normalizeId(id: string | number | null | undefined): string | null {
  if (id == null) return null;
  return String(id);
}

/**
 * Check if an ID looks like a Fulcrum UUID
 * 
 * @param id - ID to check
 * @returns true if ID matches UUID pattern
 */
export function isUUID(id: string | number): boolean {
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidPattern.test(String(id));
}

/**
 * Check if an ID looks like a Django numeric ID
 * 
 * @param id - ID to check
 * @returns true if ID is numeric (or string representation of number)
 */
export function isNumericId(id: string | number): boolean {
  return !isNaN(Number(id)) && isFinite(Number(id));
}

/**
 * Get the data source type based on ID format
 * 
 * @param id - ID to analyze
 * @returns likely data source type
 */
export function getDataSourceFromId(id: string | number): 'fulcrum' | 'django' | 'unknown' {
  if (isUUID(id)) return 'fulcrum';
  if (isNumericId(id)) return 'django';
  return 'unknown';
}
